import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Grid, List, Typography } from '@material-ui/core';
import { createStyles, darken, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';
import classNames from 'classnames';

import { DeleteSocialConnectionComponent, GetUserSocialConnectionsDocument, Network } from 'api';
import { useCurrentUser, useError } from 'hooks';
import { CustomSVG, Error } from 'components';
import { connectionPopup } from 'helpers';
import { withSocialConnections, IWithSocialConnections } from 'providers/queries';

import Connection from './Connection';

interface IPropsEnhanced extends IWithSocialConnections, WithStyles<typeof styles> {}

const styles = (theme: Theme) =>
  createStyles({
    button: {
      borderRadius: 4,
      color: theme.palette.common.white,
      fontWeight: theme.typography.fontWeightBold,
      '&:hover': {
        color: theme.palette.common.white,
      },
    },
    twitter: {
      backgroundColor: theme.custom.networks.twitter,
      '&:hover': {
        backgroundColor: darken(theme.custom.networks.twitter, 0.1),
      },
    },
    linkedin: {
      color: theme.palette.common.white,
      backgroundColor: theme.custom.networks.linkedin,
      fontWeight: theme.typography.fontWeightBold,
      '&:hover': {
        backgroundColor: darken(theme.custom.networks.linkedin, 0.1),
      },
    },
    list: {
      paddingBottom: theme.spacing(1),
    },
  });

const enhancers = compose<IPropsEnhanced, {}>(withSocialConnections(), withStyles(styles));

const Connections: FC<IPropsEnhanced> = ({ classes, connections }) => {
  const { t } = useTranslation();
  const { error, onError, onErrorClose } = useError();
  const currentUser = useCurrentUser();

  function handleConnect(provider: Network) {
    connectionPopup(currentUser.id, provider);
  }

  function renderConnections() {
    return connections.map((account, index) => (
      <DeleteSocialConnectionComponent
        key={account.id}
        variables={{ id: account.id }}
        onError={onError}
        refetchQueries={[
          { query: GetUserSocialConnectionsDocument, variables: { user_id: currentUser.id } },
        ]}>
        {deleteSocialConnection => (
          <Connection index={index} account={account} mutation={deleteSocialConnection} />
        )}
      </DeleteSocialConnectionComponent>
    ));
  }

  return (
    <Fragment>
      <Error key={error} message={error} onClose={onErrorClose} />
      <Grid container>
        <Grid item xs={3}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item>
              <CustomSVG color="action" name="activity" />
            </Grid>
            <Grid item>
              <Typography variant="subtitle2">{t('user:connectedAccounts')}</Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs>
          <List className={classes.list}>{renderConnections()}</List>
          <Grid container spacing={2}>
            <Grid item>
              <Button
                variant="contained"
                className={classNames(classes.button, classes.twitter)}
                onClick={() => handleConnect(Network.Twitter)}
                startIcon={<CustomSVG viewBox="0 0 21 20" name="twitterOutline" />}>
                {t('user:connectToTwitter')}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                className={classNames(classes.button, classes.linkedin)}
                onClick={() => handleConnect(Network.Linkedin)}
                startIcon={<CustomSVG name="linkedinOutline" />}>
                {t('user:connectToLinkedIn')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default enhancers(Connections);
